import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismBanner,
  PrismButton,
  PrismIcon,
  PrismIconBrand,
  PrismLoadingBar,
  PrismModal,
} from '@mylighthouse/prism-ember';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import type { IntlService } from 'ember-intl';
import { t } from 'ember-intl';
import type { Ota } from 'frontend-utils/models';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { Day } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { RecommendationsReasons } from 'lumen/utils/constants/visibility-boost-constants';
import { differenceInDays } from 'lumen/utils/dates/native-js-date-utils';

type ChannelItem = {
  boostUrl?: string;
  faqUrl?: string;
  name: string;
  otaId: IconBrandName;
};

interface GeneralVisibilityBoostVisibilityBoostModalSignature {
  Args: {
    day: Day;
    onClose: () => void;
  };
}

const USERSNAP_SURVEY_API_EVENT = 'visibility-boost-modal-survey';

export default class GeneralVisibilityBoostVisibilityBoostModal extends Component<GeneralVisibilityBoostVisibilityBoostModalSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: IntlService;
  @service('marketing/recommendation')
  declare marketingRecommendationService: Services['marketing/recommendation'];
  @service declare store: StoreService;
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare usersnap: Services['usersnap'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bannerMessage() {
    if (
      this.args.day.date.isBefore(
        this.hotelAndMarketService.getLocalTime()?.add(14, 'days'),
      )
    ) {
      return this.intl.t('visibility_boost.ROI_banner.message_1');
    }
    return this.intl.t('visibility_boost.ROI_banner.message_2');
  }

  get channelUrlConfigList(): ChannelItem[] {
    const data =
      this.marketingRecommendationService.fetchChannelUrlsConfigTask
        .lastSuccessful?.value?.channels ?? {};

    return Object.keys(data)
      .map((otaId) => {
        const ota = this.store.peekRecord<Ota>('ota', otaId);
        if (!ota) {
          return null;
        }

        const item: ChannelItem = {
          otaId: otaId as IconBrandName,
          name: ota.name,
          faqUrl: data[otaId]?.faq_url ?? '',
          boostUrl: data[otaId]?.boost_url ?? '',
        };

        return item;
      })
      .filter(Boolean) as ChannelItem[];
  }

  get hasRecommendation(): boolean {
    return (
      Boolean(this.marketingRecommendation) &&
      Boolean(this.marketingRecommendation?.typeSpecificData?.reason)
    );
  }

  get headerDates(): string {
    if (!this.marketingRecommendation) {
      return '';
    }

    if (this.marketingRecommendation.isSingleDay) {
      return this.marketingRecommendation.fromDate.format('D MMM YYYY');
    }

    if (
      !this.marketingRecommendation.fromDate.isSame(
        this.marketingRecommendation.endDate,
        'year',
      )
    ) {
      return `${this.marketingRecommendation.fromDate.format('D MMM YYYY')} - ${this.marketingRecommendation.endDate.format('D MMM YYYY')}`;
    }

    if (
      !this.marketingRecommendation.fromDate.isSame(
        this.marketingRecommendation.endDate,
        'month',
      )
    ) {
      return `${this.marketingRecommendation.fromDate.format('D MMM')} - ${this.marketingRecommendation.endDate.format('D MMM YYYY')}`;
    }

    return `${this.marketingRecommendation.fromDate.format('D')} - ${this.marketingRecommendation.endDate.format('D MMM YYYY')}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelId() {
    return this.hotelAndMarketService.hotel?.id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    return this.marketingRecommendationService.fetchChannelUrlsConfigTask
      .isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketingRecommendation() {
    if (this.args.day.marketingRecommendations.length === 1) {
      return this.args.day.marketingRecommendations[0];
    }

    return this.args.day.marketingRecommendations?.find((recommendation) => {
      return recommendation.fromDate.isSame(this.args.day.date, 'day');
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get recommendationText() {
    if (!this.marketingRecommendation?.typeSpecificData?.reason) {
      return '';
    }

    switch (this.marketingRecommendation?.typeSpecificData?.reason) {
      case RecommendationsReasons.LOW_OTB_DECREASE_PRICE_REC:
        return this.intl.t(
          this.marketingRecommendationService.getCachedTranslationForPriceDecrease(
            this.marketingRecommendation.fromDate.format('YYYY-MM-DD'),
          ),
        );
      case RecommendationsReasons.LOW_OTB_NO_PRICE_REC:
        return this.intl.t(
          this.marketingRecommendationService.getCachedTranslationForNoPriceRecommendation(
            this.marketingRecommendation.fromDate.format('YYYY-MM-DD'),
          ),
        );
      default:
        return '';
    }
  }

  constructor(
    owner: Owner,
    args: GeneralVisibilityBoostVisibilityBoostModalSignature['Args'],
  ) {
    super(owner, args);
    void this.initialise();
  }

  willDestroy(): void {
    super.willDestroy();

    void this.marketingRecommendationService.fetchChannelUrlsConfigTask.cancelAll();
  }

  async initialise(): Promise<void> {
    await this.marketingRecommendationService.fetchChannelUrlsConfigTask.perform(
      this.hotelId!,
    );

    const channelsConfig =
      this.marketingRecommendationService.fetchChannelUrlsConfigTask
        .lastSuccessful?.value?.channels ?? null;

    const visibleChannels = channelsConfig ? Object.keys(channelsConfig) : [];

    this.usageAnalytics.track('Visibility Boost Investigated', {
      eventTrigger: 'visibility-boost-investigate',
      product: 'pricing-manager',
      visibleChannels,
      daysOffsetFromToday: differenceInDays(
        this.args.day.date.toDate(),
        this.hotelAndMarketService.getLocalTime().toDate(),
      ),
    });
  }

  @action
  handleBoostUrlClick(boostUrl: string, channelName: string): void {
    this.usageAnalytics.track('Visibility Boost Activated', {
      eventTrigger: 'visibility-boost-activate',
      product: 'pricing-manager',
      channel: channelName,
      channelUrl: boostUrl,
      daysOffsetFromToday: differenceInDays(
        this.args.day.date.toDate(),
        this.hotelAndMarketService.getLocalTime().toDate(),
      ),
    });

    openUrl(boostUrl);

    this.usersnap.logEvent(USERSNAP_SURVEY_API_EVENT);
  }

  @action
  handleFaqUrlClick(LearnMoreUrl: string, channelName: string): void {
    this.usageAnalytics.track('Click Learn More', {
      eventTrigger: 'visibility-boost-learn-more',
      product: 'pricing-manager',
      channel: channelName,
    });

    openUrl(LearnMoreUrl);
  }

  @action
  handleLearnMoreClick(): void {
    this.usageAnalytics.track('Click Learn More', {
      eventTrigger: 'visibility-boost-learn-more',
      product: 'pricing-manager',
      channel: 'lighthouse',
    });

    this.usersnap.logEvent(USERSNAP_SURVEY_API_EVENT);
  }

  <template>
    <PrismModal @onClose={{@onClose}}>
      <:headerContent>
        <div
          class="visibility-boost-modal--header flex w-full items-center gap-200"
        >
          <h4>{{t "visibility_boost.modal.header"}}</h4>
          <span
            class="text-300--regular recommendation-days"
          >{{this.headerDates}}</span>
          <PrismBadge
            @badgeType="brand"
            @label={{t "visibility_boost.beta_badge"}}
          />
        </div>
      </:headerContent>
      <:default>
        <div class="visibility-boost-modal--content relative">
          {{#if this.isLoading}}
            <PrismLoadingBar />
          {{/if}}

          <div class="modal--padded modal--small flex flex-col gap-400">
            {{#if this.hasRecommendation}}
              <div
                class="flex flex-col gap-200"
                data-test-id="recommendation-text"
              >
                <p class="text-300--regular">{{this.recommendationText}}</p>
                <a
                  class="prism-link prism-link--standalone prism-link--medium"
                  href="https://help.mylighthouse.com/en/articles/11685283-boost-your-visibility-on-otas-and-your-own-website"
                  {{on "click" this.handleLearnMoreClick}}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{t "general.learn_more"}}
                  <PrismIcon @color="inherit" @name="book" @size="small" />
                </a>
              </div>
            {{/if}}

            <div class="flex flex-col gap-200">
              <PrismBanner
                @bannerMessage={{this.bannerMessage}}
                @bannerStyle="intelligence"
                @icon="trending-up"
                @isOneLine={{true}}
              />

              <div class="channel-list-container">
                {{#each this.channelUrlConfigList as |channel|}}
                  <div
                    class="channel-item inline-flex w-full items-baseline justify-between py-400"
                    data-test-id="channel-row-{{channel.otaId}}"
                  >
                    <span class="inline-flex gap-50">
                      <PrismIconBrand @name={{channel.otaId}} />
                      <h4>{{channel.name}}</h4>
                    </span>
                    <span class="inline-flex gap-x-200">
                      {{#if channel.faqUrl}}
                        <PrismButton
                          @buttonType="ghost"
                          @icon="book"
                          @onClick={{fn
                            this.handleFaqUrlClick
                            channel.faqUrl
                            channel.name
                          }}
                          @size="medium"
                          data-test-id="faq-button-{{channel.otaId}}"
                        />
                      {{/if}}
                      {{#if channel.boostUrl}}
                        <PrismButton
                          @buttonType="secondary"
                          @icon="link-out"
                          @label={{t
                            "visibility_boost.modal.channels.item.go_to_extranet"
                          }}
                          @onClick={{fn
                            this.handleBoostUrlClick
                            channel.boostUrl
                            channel.name
                          }}
                          @size="medium"
                          data-test-id="boost-url-button-{{channel.otaId}}"
                        />
                      {{/if}}
                    </span>
                  </div>
                {{/each}}
              </div>
            </div>

            <div class="flex gap-200">
              <p class="text-300--regular">{{t
                  "visibility_boost.own_website.text"
                }}</p>
              <a
                class="prism-link prism-link--standalone prism-link--medium"
                href="https://help.mylighthouse.com/en/articles/11685283-boost-your-visibility-on-otas-and-your-own-website#h_e9a4dd5148"
                rel="noopener noreferrer"
                target="_blank"
              >
                {{t "visibility_boost.own_website.link"}}
                <PrismIcon @color="inherit" @name="link-out" @size="small" />
              </a>
            </div>
          </div>
        </div>
      </:default>
    </PrismModal>
  </template>
}
